import Link from 'next/link'
import { cx } from '@/lib/ui'

/**
 * The mark alone: the ROUTR R, a signal path with a port at each end.
 *
 * Drawn in the 1254-unit space of v1's routr-icon-source@1254.png. The
 * favicon and the home-screen icon in public/ are this tile, path for path:
 * change one and re-render the other. The ports are real holes: the paths
 * stop at each ring's outer edge, so the tile shows through.
 */
export function LogoMark({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        'grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--ink)] text-on-ink shadow-[var(--shadow-btn)]',
        className,
      )}
    >
      <svg viewBox="319 310 600 600" className="h-[24px] w-[24px]" aria-hidden="true">
        <path
          d="M475 385H770A100 100 0 0 1 770 585H495V800M610 592L841 792"
          fill="none"
          stroke="currentColor"
          strokeWidth="55"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <g fill="none" stroke="currentColor" strokeWidth="42">
          <circle cx="410" cy="385" r="48" />
          <circle cx="890" cy="835" r="48" />
        </g>
      </svg>
    </span>
  )
}

export function Logo({ href = '/', compact = false }: { href?: string; compact?: boolean }) {
  return (
    <Link href={href} className="flex items-center gap-2.5" aria-label="ROUTR">
      <LogoMark />
      {!compact && <span className="text-[15px] font-semibold tracking-tight">ROUTR</span>}
    </Link>
  )
}

/**
 * The full ROUTR wordmark, as an inline SVG so it takes the current text
 * colour. Vector twin of brand/01-source/routr-wordmark.svg. The ports are
 * real holes — the paths stop at the ring's outer edge rather than running
 * through its centre — so the mark sits on a gradient or a photo without a
 * fill colour to match.
 */
export function Wordmark({ className }: { className?: string }) {
  return (
    <svg viewBox="95 300 1590 350" className={className} role="img" aria-label="ROUTR">
      <g fill="none" stroke="currentColor" strokeWidth="28" strokeLinecap="round" strokeLinejoin="round">
        <path d="M171 345H400A42.5 42.5 0 0 1 400 430H250V532M300 430L425 532" />
        <rect x="505" y="345" width="270" height="187" rx="93.5" />
        <path d="M845 345V440A100 92 0 0 0 1045 440V345" />
        <path d="M1105 345H1300M1202 345V532" />
        <path d="M1355 345H1470A42.5 42.5 0 0 1 1470 430H1355V532M1405 430L1611 579" />
      </g>
      <g fill="none" stroke="currentColor" strokeWidth="24">
        <circle cx="135" cy="345" r="24" />
        <circle cx="1640" cy="600" r="24" />
      </g>
    </svg>
  )
}
